import React, { useState } from "react";

import {
  Box,
  Typography,
  Avatar,
  Divider,
  Skeleton,
  Switch,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";

import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useMsal } from "@azure/msal-react";

import { useUserRole } from "../context/UserRoleContext";
import { useColorMode } from "../context/ColorModeContext";
import { getRoleColor } from "../auth/roles";
import uiConfig from "../config/uiConfig";

const formatRoleLabel = (roleName) =>
  (roleName || "member").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function DetailRow({ icon, label, value, isLast = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.6,
        borderBottom: isLast ? "none" : "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          background: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "text.primary", fontWeight: 600 }}>
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );
}

function Settings() {
  const { employee, role, loading, refreshUserRole } = useUserRole();
  const { mode, toggleColorMode } = useColorMode();
  const { instance } = useMsal();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const initials = (employee?.employee_name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "U";

  const roleColor = getRoleColor(role);

  const handleRefreshAccess = async () => {
    try {
      setIsRefreshing(true);
      await refreshUserRole();
      setSnackbar({ open: true, message: "Your access has been refreshed", severity: "success" });
    } catch (error) {
      console.error("Refresh Access Error:", error);
      setSnackbar({ open: true, message: "Failed to refresh access", severity: "error" });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: 3 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: "text.primary", mb: 0.4 }}>
        My Profile
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 3 }}>
        View your account and employment details.
      </Typography>

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Skeleton variant="circular" width={72} height={72} />
              <Box>
                <Skeleton variant="text" width={180} height={28} />
                <Skeleton variant="text" width={120} height={20} />
              </Box>
            </Box>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={`settings-skeleton-${index}`} variant="text" height={40} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                px: { xs: 3, md: 5 },
                py: 4,
                background: uiConfig.tableAccentGradient,
              }}
            >
              <Avatar
                src={employee?.profile_picture || undefined}
                sx={{
                  width: 72,
                  height: 72,
                  fontSize: 26,
                  fontWeight: 700,
                  background: "rgba(255,255,255,0.25)",
                  color: "#ffffff",
                  border: "3px solid rgba(255,255,255,0.4)",
                }}
              >
                {initials}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#ffffff" }} noWrap>
                  {employee?.employee_name || "N/A"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }} noWrap>
                  {employee?.email || "N/A"}
                </Typography>

                <Box
                  sx={{
                    display: "inline-block",
                    mt: 1,
                    px: 1.4,
                    py: 0.3,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    background: roleColor.background,
                    color: roleColor.color,
                  }}
                >
                  {formatRoleLabel(role)}
                </Box>
              </Box>
            </Box>

            <Divider />

            <Box
              sx={{
                px: { xs: 3, md: 5 },
                py: 1,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 4,
              }}
            >
              <DetailRow
                icon={<TagOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
                label="Employee ID"
                value={employee?.employee_id}
              />

              <DetailRow
                icon={<EmailOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
                label="Email"
                value={employee?.email}
              />

              <DetailRow
                icon={<WorkOutlineOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
                label="Designation"
                value={employee?.designation}
              />

              <DetailRow
                icon={<ApartmentOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
                label="Department"
                value={employee?.department_name}
              />

              <DetailRow
                icon={<BadgeOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
                label="Role"
                value={formatRoleLabel(role)}
                isLast
              />

              <DetailRow
                icon={<SupervisorAccountOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
                label="Manager"
                value={employee?.manager_name || "No manager"}
                isLast
              />
            </Box>
          </>
        )}
      </Box>

      <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary", mt: 4, mb: 0.4 }}>
        Preferences
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 2 }}>
        Manage your appearance and account preferences.
      </Typography>

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 3, md: 5 },
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {mode === "dark" ? (
                <DarkModeOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              ) : (
                <LightModeOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                Appearance
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                {mode === "dark" ? "Dark mode is on" : "Light mode is on"}
              </Typography>
            </Box>
          </Box>

          <Switch checked={mode === "dark"} onChange={toggleColorMode} />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 3, md: 5 },
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <RefreshOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                Refresh My Access
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                Reload your role and page permissions if they were recently changed.
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={handleRefreshAccess}
            disabled={isRefreshing}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, flexShrink: 0 }}
          >
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 3, md: 5 },
            py: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LogoutOutlinedIcon sx={{ fontSize: 18, color: "#dc2626" }} />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                Log Out
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                Sign out of your account on this device.
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={handleLogout}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, flexShrink: 0 }}
          >
            Log Out
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;
